import moment from 'moment';
import { SurveyType } from '../../../../../shared/constants';
import {
  SURVEY_SCALE_BY_TYPE,
  surveyScoreToLabel,
} from '../../../../../shared/surveys';
import type { CustomerSurveyResponse } from '../../../../../store/customers/customerTypes';
import type {
  SurveyInsightsBlock,
  SurveyInsightsDistributionEntry,
  SurveyInsightsGranularity,
  SurveyInsightsTrendBucket,
} from '../../../../../store/hubOwner/types';

export type SurveyTrendDirection =
  'improving' | 'declining' | 'steady' | 'insufficient';

export type SurveyStats = {
  // Days answered inside the selected window (latest answer per day).
  count: number;
  // Mean score over the window (API `average_score`); null with no responses.
  average: number | null;
  // Lowest / highest score answered in the window, from the distribution.
  lowest: SurveyInsightsDistributionEntry | null;
  highest: SurveyInsightsDistributionEntry | null;
  // Most recent response of this type inside the window.
  latest: CustomerSurveyResponse | null;
  trend: SurveyTrendDirection;
  // Weighted mean of the recent half of the answered buckets minus the
  // earlier half (one decimal).
  trendDelta: number | null;
};

// One point on the trend line: an answered bucket from
// GET /owners/:id/survey_insights with `ts` (epoch ms of bucket_start, local)
// for the time axis. `count` is answered days in the bucket (1 for a daily
// bucket, up to 7 for a weekly one).
export type SurveyTrendPoint = {
  ts: number;
  bucket_start: string;
  bucket_end: string;
  average: number;
  count: number;
};

// Fewer answered buckets than this and a direction would be noise.
export const MIN_BUCKETS_FOR_TREND = 4;
// Half-score shifts between the two halves count as a real change.
export const TREND_DELTA_THRESHOLD = 0.5;

export type InsightsWindow = {
  days: number;
  label: string;
  // Trend bucket size requested from the API for this window.
  granularity: SurveyInsightsGranularity;
};

// Rolling windows offered on the tab (API max 92 days). `days` is sent to the
// API as-is: "Last 7 days" is today plus the 6 days before it (Oct 2 – Oct 8
// on Oct 8), the way Grafana, Sentry and Metabase count it. Daily buckets up
// to a month, weekly for the quarter so the line stays readable.
export const INSIGHTS_WINDOWS: InsightsWindow[] = [
  { days: 7, label: 'Last 7 days', granularity: 'day' },
  { days: 30, label: 'Last 30 days', granularity: 'day' },
  { days: 90, label: 'Last 90 days', granularity: 'week' },
];
export const DEFAULT_INSIGHTS_WINDOW: InsightsWindow = INSIGHTS_WINDOWS[1];

// Rows placeholder shared across renders so memoised consumers see a stable
// reference when a survey type has no responses.
export const NO_SURVEY_ROWS: readonly CustomerSurveyResponse[] = Object.freeze(
  [],
);

// Plural noun for one trend bucket: 'days' or 'weeks'.
export const bucketUnitLabel = (
  granularity: SurveyInsightsGranularity | null | undefined,
): string => (granularity === 'week' ? 'weeks' : 'days');

export const CHART_HEIGHT = 280;

// 'Sep 25, 2026' for a daily bucket, 'Sep 25 – Oct 1, 2026' for a weekly one.
export const formatBucketRange = (point: {
  bucket_start: string;
  bucket_end: string;
}): string => {
  const start = moment(point.bucket_start, 'YYYY-MM-DD', true);
  const end = moment(point.bucket_end, 'YYYY-MM-DD', true);
  if (!end.isValid() || end.isSame(start, 'day')) {
    return start.format('MMM D, YYYY');
  }
  return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
};

// Fewest ticks before the x axis starts thinning to every Nth day.
export const MAX_TREND_AXIS_TICKS = 8;

// Epoch-ms ticks for the trend chart's x axis across the resolved window: one
// per day for a week, thinned to at most MAX_TREND_AXIS_TICKS for longer
// windows, always including both window edges. Without this recharts ticks a
// numeric x axis at the data points, so a window with one answered bucket
// gets one tick, under that dot, and the span is unreadable. Undefined (let
// recharts decide) when the period is missing or does not parse.
export const buildTrendAxisTicks = (
  period: { from: string; to: string } | null | undefined,
  maxTicks: number = MAX_TREND_AXIS_TICKS,
): number[] | undefined => {
  if (!period) return undefined;
  const from = localDayMs(period.from);
  const to = localDayMs(period.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || from > to) {
    return undefined;
  }
  const days = moment(to).diff(moment(from), 'days');
  const step = Math.max(1, Math.ceil(days / (maxTicks - 1)));
  const ticks: number[] = [];
  for (let d = 0; d < days; d += step) {
    // Skip a tick that would crowd the final one.
    if (days - d < step / 2) break;
    ticks.push(moment(from).add(d, 'days').valueOf());
  }
  ticks.push(to);
  return ticks;
};

// 'Sep 28 – Oct 5, 2026' for the resolved window; null when the period is
// missing or its dates do not parse.
export const formatPeriodRange = (
  period: { from: string; to: string } | null | undefined,
): string | null => {
  if (!period) return null;
  const from = moment(period.from, 'YYYY-MM-DD', true);
  const to = moment(period.to, 'YYYY-MM-DD', true);
  if (!from.isValid() || !to.isValid()) return null;
  const fromFmt = from.year() === to.year() ? 'MMM D' : 'MMM D, YYYY';
  return `${from.format(fromFmt)} – ${to.format('MMM D, YYYY')}`;
};

const receivedAtMs = (row: CustomerSurveyResponse): number =>
  new Date(row.received_at).getTime();

const hasValidDate = (row: CustomerSurveyResponse): boolean =>
  !!row?.received_at && Number.isFinite(receivedAtMs(row));

// API dates are calendar days ('YYYY-MM-DD') in the zone the request named,
// i.e. the browser's; parse them as local dates so a day never shifts.
export const localDayMs = (day: string): number =>
  moment(day, 'YYYY-MM-DD', true).valueOf();

// Label for a row: the API's `response` when present, else derived from the
// score on the survey type's scale.
export const surveyRowLabel = (
  row: CustomerSurveyResponse,
  surveyType: SurveyType,
): string => row.response || surveyScoreToLabel(surveyType, row.score) || 'N/A';

export const sortByReceivedAt = (
  rows: readonly CustomerSurveyResponse[],
  direction: 'asc' | 'desc' = 'asc',
): CustomerSurveyResponse[] =>
  [...rows].sort((a, b) =>
    direction === 'asc'
      ? receivedAtMs(a) - receivedAtMs(b)
      : receivedAtMs(b) - receivedAtMs(a),
  );

// Buckets the mixed-type rows from GET /customers/:id/satisfaction_survey by
// survey_type_id. Rows without a recognised type are dropped.
export const groupRowsBySurveyType = (
  rows: CustomerSurveyResponse[] | null | undefined,
): Partial<Record<SurveyType, CustomerSurveyResponse[]>> => {
  const groups: Partial<Record<SurveyType, CustomerSurveyResponse[]>> = {};
  (rows ?? []).forEach((row) => {
    const type = Number(row?.survey_type_id) as SurveyType;
    if (!SURVEY_SCALE_BY_TYPE[type]) return;
    (groups[type] ??= []).push(row);
  });
  return groups;
};

export const findSurveyBlock = (
  surveys: SurveyInsightsBlock[] | null | undefined,
  surveyType: SurveyType,
): SurveyInsightsBlock | undefined =>
  (surveys ?? []).find((b) => Number(b?.survey_type_id) === surveyType);

const round1 = (value: number): number => Math.round(value * 10) / 10;

// Points for the trend chart, in bucket order. Surveys are answered
// sporadically, so buckets with no responses are dropped rather than plotted
// as gaps: the line then runs from one answered bucket to the next, which is
// the trend a reader expects (daily buckets would otherwise be lone dots).
// Buckets with an unparsable start date cannot be placed and are skipped.
export const buildTrendPoints = (
  buckets: SurveyInsightsTrendBucket[] | null | undefined,
): SurveyTrendPoint[] =>
  (buckets ?? [])
    .filter(
      (b) => b.average_score != null && (Number(b.response_count) || 0) > 0,
    )
    .map((b) => ({
      ts: localDayMs(b.bucket_start),
      bucket_start: b.bucket_start,
      bucket_end: b.bucket_end,
      average: Number(b.average_score),
      count: Number(b.response_count) || 0,
    }))
    .filter((p) => Number.isFinite(p.ts));

// Weighted mean (by response_count) of the bucket averages.
const weightedMean = (buckets: SurveyInsightsTrendBucket[]): number => {
  const total = buckets.reduce(
    (s, b) => s + (Number(b.response_count) || 0),
    0,
  );
  const sum = buckets.reduce(
    (s, b) =>
      s + (Number(b.average_score) || 0) * (Number(b.response_count) || 0),
    0,
  );
  return total ? sum / total : 0;
};

// Compares the recent half of the answered buckets with the earlier half.
// Needs MIN_BUCKETS_FOR_TREND answered buckets to say anything.
export const computeTrend = (
  buckets: SurveyInsightsTrendBucket[] | null | undefined,
): Pick<SurveyStats, 'trend' | 'trendDelta'> => {
  const answered = (buckets ?? []).filter(
    (b) => b.average_score != null && (Number(b.response_count) || 0) > 0,
  );
  if (answered.length < MIN_BUCKETS_FOR_TREND) {
    return { trend: 'insufficient', trendDelta: null };
  }
  const half = Math.floor(answered.length / 2);
  const earlier = answered.slice(0, answered.length - half);
  const recent = answered.slice(answered.length - half);
  const delta = round1(weightedMean(recent) - weightedMean(earlier));
  if (delta >= TREND_DELTA_THRESHOLD) {
    return { trend: 'improving', trendDelta: delta };
  }
  if (delta <= -TREND_DELTA_THRESHOLD) {
    return { trend: 'declining', trendDelta: delta };
  }
  return { trend: 'steady', trendDelta: delta };
};

export const computeSurveyStats = (
  block: SurveyInsightsBlock | null | undefined,
  rows: readonly CustomerSurveyResponse[] | null | undefined,
): SurveyStats => {
  const answered = (block?.distribution ?? []).filter(
    (d) => (Number(d.count) || 0) > 0,
  );
  const dated = (rows ?? []).filter(hasValidDate);
  const latest = dated.length
    ? sortByReceivedAt(dated, 'desc')[0]
    : (rows?.[0] ?? null);
  return {
    count: Number(block?.response_count) || 0,
    average: block?.average_score == null ? null : Number(block.average_score),
    lowest: answered.length
      ? answered.reduce((a, b) => (b.score < a.score ? b : a))
      : null,
    highest: answered.length
      ? answered.reduce((a, b) => (b.score > a.score ? b : a))
      : null,
    latest,
    ...computeTrend(block?.trend),
  };
};
